import type { ReactNode } from 'react'

import { errorMeta, type ErrorMetaSource } from './errorMeta'
import { StatusMessage, type StatusKind } from './StatusMessage'

/**
 * Ошибка команды (записи) одним блоком: заголовок, текст сервера дословно (ADR-8), служебная строка «Код … · запрос …»
 * и кнопка копирования (её рисует {@link StatusMessage}; правило проекта 19.10.2026). Блок не исчезает по таймеру.
 * Вид панели выбирается по виду ошибки: нет входа, нет доступа, служба недоступна и неизвестный исход — свои значки,
 * остальное — «ошибка».
 */

/** Ошибка записи в форме, нужной блоку; структурно подходит `ErrorView` клиентской среды выполнения. */
export interface ErrorNoticeError extends ErrorMetaSource {
  message: string
  /** Вид ошибки: `unauthorized`, `forbidden`, `unavailable`, `unknown-outcome`; любой другой — «ошибка». */
  kind?: string
}

export interface ErrorNoticeProps {
  error: ErrorNoticeError
  /** Заголовок по умолчанию; для отдельных видов ошибки его можно заменить через `titles`. */
  title: string
  /** Заголовки по виду ошибки (`kind`). */
  titles?: Readonly<Record<string, string>>
  /** Кнопки под текстом (например, «Повторить» или «Перечитать»). */
  action?: ReactNode
}

const PANEL_KIND: Readonly<Record<string, StatusKind>> = {
  unauthorized: 'unauthorized',
  forbidden: 'forbidden',
  unavailable: 'unavailable',
  'unknown-outcome': 'unavailable',
}

export function ErrorNotice({ error, title, titles, action }: ErrorNoticeProps) {
  const kind = error.kind !== undefined ? error.kind : ''
  const meta = errorMeta(error)
  return (
    <StatusMessage
      kind={PANEL_KIND[kind] ?? 'error'}
      title={titles?.[kind] ?? title}
      text={error.message}
      {...(meta !== undefined ? { meta } : {})}
      {...(action !== undefined ? { action } : {})}
    />
  )
}
